/**
 * React Query hooks for agent binary downloads.
 *
 * Sprint: 73, Task: S73-B01
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type { AgentReleaseInfo, AgentLatestVersion } from '../agent-download-types';

// =============================================================================
// Query Keys
// =============================================================================

export const agentDownloadKeys = {
  all: ['agent-downloads'] as const,
  latestVersion: () => [...agentDownloadKeys.all, 'latest-version'] as const,
  release: (version: string) => [...agentDownloadKeys.all, 'release', version] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Get the latest agent version.
 */
export function useLatestAgentVersion() {
  return useQuery({
    queryKey: agentDownloadKeys.latestVersion(),
    queryFn: async () => {
      const { data } = await apiClient.get<AgentLatestVersion>('/api/v1/agent-downloads/latest/version.json');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 min
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/**
 * Get release info including all binaries.
 */
export function useAgentRelease(version: string) {
  return useQuery({
    queryKey: agentDownloadKeys.release(version),
    queryFn: async () => {
      const { data } = await apiClient.get<AgentReleaseInfo>(`/api/v1/agent-downloads/${version}/info`);
      return data;
    },
    enabled: !!version,
    staleTime: 10 * 60 * 1000, // 10 min
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/**
 * Get the install script URL (for display, not fetched via React Query).
 */
export function getInstallScriptUrl(): string {
  return '/api/v1/agent-downloads/install.sh';
}
